import { firestore } from '@/firebase';
import { Phase, PhaseHoursBreakdown, Activity, ActualActivity, ContentAssignment, ContentItem } from '@/types';
import { collection, getDocs, getDoc, query, where, doc, Timestamp } from 'firebase/firestore';

/**
 * PhaseHoursCalculator - Calculates hours breakdown per phase
 * Separates activity hours, screencast hours, and webinar hours
 * Part of the content/webinar decoupling refactor
 */
export class PhaseHoursCalculator {
  
  /**
   * Calculate planned hours for a phase (from template configuration)
   * Falls back to summing activity plannedHours if phase-level fields aren't set (backward compatibility)
   * @param phase The phase object
   * @param activities All activities (optional, for backward compatibility)
   * @returns Breakdown of planned hours by category
   */
  static getPlannedHours(phase: Phase, activities?: Activity[]): PhaseHoursBreakdown {
    // If phase-level content hours are set, we're in the new system.
    // screencastHours/webinarHours are authoritative; for activityHours use phase.hours
    // when it is explicitly set (> 0), otherwise fall back to summing the remaining
    // activities so that phases migrated by the cleanup script (which never touched
    // phase.hours) still show the correct activity-hour denominator.
    if (phase.hours || phase.screencastHours || phase.webinarHours) {
      let activityHours = phase.hours || 0;

      if (activityHours === 0 && activities) {
        const phaseActivities = activities.filter(a => a.phaseId === phase.id);
        activityHours = phaseActivities
          .filter(a => {
            const n = (a.name || '').toLowerCase();
            return !n.includes('screencast') && !n.includes('webinair') && !a.isContentActivity;
          })
          .reduce((sum, a) => {
            const h = typeof a.plannedHours === 'string' ? parseFloat(a.plannedHours) : a.plannedHours || 0;
            return sum + h;
          }, 0);
      }

      return {
        activityHours,
        screencastHours: phase.screencastHours || 0,
        webinarHours: phase.webinarHours || 0
      };
    }
    
    // Otherwise, fall back to summing activity plannedHours (old system - backward compatibility)
    if (activities) {
      const phaseActivities = activities.filter(a => a.phaseId === phase.id);
      
      let activityHours = 0;
      let screencastHours = 0;
      let webinarHours = 0;
      
      for (const activity of phaseActivities) {
        const plannedHours = typeof activity.plannedHours === 'string' 
          ? parseFloat(activity.plannedHours) 
          : activity.plannedHours || 0;
        
        // Categorize by activity name (legacy system detection)
        const name = activity.name?.toLowerCase() || '';
        if (name.includes('screencast')) {
          screencastHours += plannedHours;
        } else if (name.includes('webinair') || name.includes('webinar')) {
          webinarHours += plannedHours;
        } else if (!activity.isContentActivity) {
          // Regular activity (not screencast/webinar)
          activityHours += plannedHours;
        }
      }
      
      return { activityHours, screencastHours, webinarHours };
    }
    
    // No data available
    return { activityHours: 0, screencastHours: 0, webinarHours: 0 };
  }

  /**
   * Calculate actual hours for a phase for a specific user
   * @param phase The phase object
   * @param userId The participant user ID
   * @param activities All activities in the program
   * @param actualActivities All actual activities for the participant
   * @returns Breakdown of actual hours by category
   */
  static async calculateActualHours(
    phase: Phase,
    userId: string,
    activities: Activity[],
    actualActivities: ActualActivity[]
  ): Promise<PhaseHoursBreakdown> {
    
    // 1. Calculate activity hours, categorized by type
    const phaseActivities = activities.filter(a => a.phaseId === phase.id);
    
    let activityHours = 0;
    let legacyScreencastHours = 0;
    let legacyWebinarHours = 0;
    
    for (const activity of phaseActivities) {
      const activityActuals = actualActivities.filter(aa => 
        aa.activityId === activity.id &&
        aa.participantConfirmed  // Only count confirmed hours
      );
      
      const hoursForActivity = activityActuals.reduce((sum, aa) => 
        sum + parseFloat(aa.actualHours.toString()), 0
      );
      
      // Categorize by activity name (backward compatibility)
      const name = activity.name?.toLowerCase() || '';
      if (name.includes('screencast')) {
        legacyScreencastHours += hoursForActivity;
      } else if (name.includes('webinar')) {
        legacyWebinarHours += hoursForActivity;
      } else {
        activityHours += hoursForActivity;
      }
    }

    // New system: if phase has template-level hours, screencast/webinar count directly ("Direct geteld")
    const isNewSystem = !!(phase.hours || phase.screencastHours || phase.webinarHours);
    if (isNewSystem) {
      const planned = PhaseHoursCalculator.getPlannedHours(phase);
      return {
        activityHours,
        screencastHours: planned.screencastHours,
        webinarHours: planned.webinarHours,
      };
    }

    // Old system: calculate screencast hours from content-assignments
    const newScreencastHours = await this.calculateScreencastHours(phase.id!, userId);

    // Old system: calculate webinar hours from event-attendees
    const newWebinarHours = await this.calculateWebinarHours(phase, userId);

    return {
      activityHours,
      screencastHours: legacyScreencastHours + newScreencastHours,
      webinarHours: legacyWebinarHours + newWebinarHours
    };
  }

  /**
   * Calculate screencast hours for a phase
   * Sums hourValue from content-assignments where phase matches
   * @param phaseId The phase ID
   * @param userId The participant user ID
   * @returns Total screencast hours
   */
  private static async calculateScreencastHours(phaseId: string, userId: string): Promise<number> {
    try {
      const assignmentsQuery = query(
        collection(firestore, 'content-assignments'),
        where('user', '==', userId),
        where('phase', '==', phaseId)
      );
      
      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      let totalHours = 0;

      for (const assignmentDoc of assignmentsSnapshot.docs) {
        const assignment = assignmentDoc.data() as ContentAssignment;
        
        // Skip if not completed (unless autoCompleteHours is true)
        if (!assignment.completed && !assignment.autoCompleteHours) {
          continue;
        }

        // Use snapshot hourValue if available, otherwise fetch from content-item
        let hourValue = assignment.hourValue;
        
        if (!hourValue && assignment.contentId) {
          try {
            const contentDoc = await getDoc(doc(firestore, 'content-items', assignment.contentId));
            if (contentDoc.exists()) {
              const contentData = contentDoc.data() as ContentItem;
              hourValue = contentData.hourValue || 1; // Default to 1 hour
            }
          } catch (error) {
            console.warn('Error fetching content-item for hourValue:', error);
            hourValue = 1; // Fallback to 1 hour
          }
        }

        totalHours += hourValue || 1;
      }

      return totalHours;
    } catch (error) {
      console.error('Error calculating screencast hours:', error);
      return 0;
    }
  }

  /**
   * Calculate webinar hours for a phase
   * Counts webinar registrations where webinar date falls within phase dates
   * @param phase The phase object
   * @param userId The participant user ID
   * @returns Total webinar hours
   */
  private static async calculateWebinarHours(phase: Phase, userId: string): Promise<number> {
    try {
      // Get all event-attendees for this user
      const attendeesQuery = query(
        collection(firestore, 'event-attendees'),
        where('user', '==', userId)
      );
      
      const attendeesSnapshot = await getDocs(attendeesQuery);
      let totalHours = 0;

      const phaseStart = phase.start.toDate();
      const phaseEnd = phase.end.toDate();

      for (const attendeeDoc of attendeesSnapshot.docs) {
        const attendee = attendeeDoc.data();
        
        // Only count if present (attendance marked)
        if (!attendee.present) {
          continue;
        }

        // Fetch the webinar to check its date
        if (attendee.webinarId) {
          try {
            const webinarDoc = await getDoc(doc(firestore, 'webinars', attendee.webinarId));
            if (webinarDoc.exists()) {
              const webinar = webinarDoc.data();
              const webinarDate = webinar.start.toDate();

              // Check if webinar falls within phase dates
              if (webinarDate >= phaseStart && webinarDate <= phaseEnd) {
                totalHours += 2; // Default 2 hours per webinar
              }
            }
          } catch (error) {
            console.warn('Error fetching webinar:', error);
          }
        }
      }

      return totalHours;
    } catch (error) {
      console.error('Error calculating webinar hours:', error);
      return 0;
    }
  }

  /**
   * Calculate total hours (sum of all categories)
   * @param breakdown PhaseHoursBreakdown object
   * @returns Total hours
   */
  static getTotalHours(breakdown: PhaseHoursBreakdown): number {
    return breakdown.activityHours + breakdown.screencastHours + breakdown.webinarHours;
  }

  /**
   * Calculate completion percentage for a phase
   * @param planned Planned hours breakdown
   * @param actual Actual hours breakdown
   * @returns Percentage (0-100)
   */
  static calculateCompletionPercentage(
    planned: PhaseHoursBreakdown,
    actual: PhaseHoursBreakdown
  ): number {
    const totalPlanned = this.getTotalHours(planned);
    const totalActual = this.getTotalHours(actual);
    
    if (totalPlanned === 0) return 0;
    
    return Math.min(Math.round((totalActual / totalPlanned) * 100), 100);
  }
}
